import React, { useState } from 'react';
import api from '../services/api.ts';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { formatCurrency } from '../utils/currency.ts';
import { formatStatus } from '../utils/statusFormatter.ts';
import ConfirmationModal from '../components/ConfirmationModal.tsx';
import DetailShell from '../components/ui/DetailShell.tsx';
import StatusChip from '../components/ui/StatusChip.tsx';
import ImageGallery from '../components/ImageGallery.tsx';
import { formatDateOnly } from '../utils/date.ts';
import { formatPlate } from '../utils/licensePlate.ts';
import { assetTypeLabel } from '../utils/labels.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { DocumentTextIcon, PhotoIcon } from '@heroicons/react/24/outline';

const getBackendUrl = (path: string) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
  return `${baseUrl}${path}`;
};

const ATTACHMENT_CATEGORY_LABELS: Record<string, string> = {
  crlv: 'CRLV',
  manual: 'Manual',
  nota_fiscal: 'Nota fiscal',
  fotos: 'Fotos',
  outros: 'Outros',
  contrato: 'Contrato',
  escritura: 'Escritura',
  projeto_engenharia: 'Projeto de engenharia',
  projeto_arquitetura: 'Projeto de arquitetura',
};

const attachmentCategoryLabel = (slug?: string | null) =>
  !slug ? '—' : ATTACHMENT_CATEGORY_LABELS[slug] || slug;

const toneFor = (s: string): 'green' | 'amber' | 'slate' | 'blue' | 'red' => {
  if (s === 'in_stock' || s === 'active') return 'green';
  if (s === 'reserved' || s === 'maintenance') return 'amber';
  if (s === 'sold') return 'slate';
  if (s === 'exchanged') return 'blue';
  return 'slate';
};

const VehicleDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState<'details' | 'history' | 'related' | 'attachments'>(
    'details'
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [galleryStartIndex, setGalleryStartIndex] = useState(0);

  const { data: vehicle, isLoading, error } = useQuery(
    ['vehicle', id],
    async () => {
      const response = await api.get(`/vehicles/${id}`);
      if (response.data.attachments && Array.isArray(response.data.attachments)) {
        response.data.attachments = response.data.attachments.map((attachment: any) => {
          const type =
            attachment.type ||
            (attachment.mimetype?.startsWith('image/') ? 'image' : 'document');
          return {
            ...attachment,
            type,
            preview:
              type === 'image' && attachment.url ? getBackendUrl(attachment.url) : undefined,
            file: new File([], attachment.name || 'file'),
          };
        });
      } else {
        response.data.attachments = [];
      }
      return response.data;
    },
    { enabled: !!id }
  );

  const { data: history, isLoading: historyLoading } = useQuery(
    ['vehicle-history', id],
    async () => (await api.get(`/vehicles/${id}/history`)).data,
    { enabled: !!id && activeTab === 'history' }
  );

  const { data: relatedAssets } = useQuery<any[]>(
    ['related-vehicle', id],
    async () => (await api.get(`/vehicles/${id}/related`)).data,
    { enabled: !!id }
  );

  const { data: assetRelatedAssets } = useQuery<any[]>(
    ['related-assets', vehicle?.assetId],
    async () => {
      if (!vehicle?.assetId) return [];
      return (await api.get(`/assets/${vehicle.assetId}/related`)).data;
    },
    { enabled: !!vehicle?.assetId }
  );

  const deleteMutation = useMutation(async () => api.delete(`/vehicles/${id}`), {
    onSuccess: () => {
      queryClient.invalidateQueries(['vehicles']);
      navigate('/vehicles');
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }
  if (error || !vehicle) {
    return <div className="field-error">Veículo não encontrado.</div>;
  }

  const related =
    relatedAssets && relatedAssets.length > 0 ? relatedAssets : assetRelatedAssets || [];
  const inStock = vehicle.status === 'in_stock';
  const purchase = Number(vehicle.purchasePrice || 0);
  const selling = vehicle.sellingPrice != null ? Number(vehicle.sellingPrice) : null;

  return (
    <DetailShell
      breadcrumb={`Patrimônio / Estoque p/ venda / ${vehicle.make} ${vehicle.model}`}
      title={`${vehicle.make} ${vehicle.model}`}
      subtitle={`Placa ${formatPlate(vehicle.licensePlate)} · ${vehicle.year}`}
      statusLabel={formatStatus(String(vehicle.status))}
      statusTone={toneFor(String(vehicle.status))}
      backLabel="Voltar ao estoque"
      onBack={() => navigate('/vehicles')}
      actions={
        <>
          {hasPermission('assets.update') && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate(`/vehicles/${id}/editar`)}
            >
              Editar
            </button>
          )}
          {inStock && hasPermission('assets.update') && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate(`/vehicles/${id}/sale`)}
            >
              Registrar venda
            </button>
          )}
          {hasPermission('assets.delete') && (
            <button type="button" className="btn btn-danger" onClick={() => setShowDeleteModal(true)}>
              Excluir
            </button>
          )}
        </>
      }
      tabs={
        <div className="tabs" style={{ marginBottom: 12 }}>
          {(
            [
              ['details', 'Resumo'],
              ['history', 'Histórico'],
              ['related', 'Relacionados'],
              ['attachments', 'Anexos'],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`tab${activeTab === key ? ' active' : ''}`}
              onClick={() => setActiveTab(key)}
            >
              {label}
            </button>
          ))}
        </div>
      }
    >
      {activeTab === 'details' && (
        <>
          <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <div className="kpi">
              <div className="kpi-label">Custo de compra</div>
              <div className="kpi-value">{formatCurrency(purchase)}</div>
            </div>
            <div className="kpi">
              <div className="kpi-label">Preço de venda</div>
              <div className="kpi-value">{selling != null ? formatCurrency(selling) : '—'}</div>
            </div>
            <div className="kpi">
              <div className="kpi-label">Cor</div>
              <div className="kpi-value" style={{ fontSize: 18 }}>
                {vehicle.color || '—'}
              </div>
            </div>
          </div>

          <div className="card invest-detail-card" style={{ marginBottom: 12 }}>
            <div className="card-header">
              <div className="card-title">Dados do veículo</div>
              {hasPermission('assets.update') && (
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => navigate(`/vehicles/${id}/editar`)}
                >
                  Editar
                </button>
              )}
            </div>
            <dl className="detail-fields">
              <div>
                <dt>Marca</dt>
                <dd>{vehicle.make}</dd>
              </div>
              <div>
                <dt>Modelo</dt>
                <dd>{vehicle.model}</dd>
              </div>
              <div>
                <dt>Placa</dt>
                <dd className="mono">{formatPlate(vehicle.licensePlate)}</dd>
              </div>
              <div>
                <dt>Ano · Cor</dt>
                <dd>
                  {vehicle.year} · {vehicle.color || '—'}
                </dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>
                  <StatusChip
                    label={formatStatus(String(vehicle.status))}
                    tone={toneFor(String(vehicle.status))}
                  />
                </dd>
              </div>
              <div>
                <dt>Data de compra</dt>
                <dd className="mono">
                  {vehicle.purchaseDate ? formatDateOnly(vehicle.purchaseDate) : '—'}
                </dd>
              </div>
              <div>
                <dt>Data de venda</dt>
                <dd className="mono">
                  {vehicle.soldDate ? formatDateOnly(vehicle.soldDate) : '—'}
                </dd>
              </div>
              <div>
                <dt>Custo de compra</dt>
                <dd>{formatCurrency(purchase)}</dd>
              </div>
              <div>
                <dt>Preço de venda</dt>
                <dd>{selling != null ? formatCurrency(selling) : '—'}</dd>
              </div>
            </dl>
          </div>

          {hasPermission('assets.update') && vehicle.status !== 'sold' && vehicle.status !== 'exchanged' && (
            <div className="toolbar" style={{ marginBottom: 12 }}>
              <div className="filters" />
              <div className="detail-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() =>
                    navigate(`/exchanges/new?originType=vehicle&originId=${id}`)
                  }
                >
                  Registrar permuta
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === 'history' && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Histórico</div>
          </div>
          {historyLoading ? (
            <div className="muted" style={{ padding: 32, textAlign: 'center' }}>
              Carregando…
            </div>
          ) : !history || history.length === 0 ? (
            <div className="muted" style={{ padding: 32, textAlign: 'center' }}>
              Sem histórico registrado.
            </div>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Evento</th>
                    <th>Detalhe</th>
                  </tr>
                </thead>
                <tbody>
                  {(history as any[]).map((h: any, i: number) => (
                    <tr key={h.id || i}>
                      <td className="mono">
                        {h.createdAt || h.timestamp
                          ? new Date(h.createdAt || h.timestamp).toLocaleString('pt-BR')
                          : '—'}
                      </td>
                      <td>{h.action || h.type || h.event || 'Atualização'}</td>
                      <td>{h.description || h.detail || h.message || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'attachments' && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Anexos</div>
            <button
              type="button"
              className="link-btn"
              onClick={() => navigate(`/vehicles/${id}/editar`)}
            >
              Adicionar / editar
            </button>
          </div>
          <div style={{ padding: 20 }}>
            <h4 className="muted" style={{ marginBottom: 12, display: 'flex', gap: 6, alignItems: 'center' }}>
              <PhotoIcon style={{ width: 16, height: 16 }} /> Fotos
            </h4>
            {(vehicle.attachments || []).filter((a: any) => a.type === 'image').length > 0 ? (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                  gap: 10,
                  marginBottom: 24,
                }}
              >
                {(vehicle.attachments || [])
                  .filter((a: any) => a.type === 'image')
                  .map((photo: any, index: number) => (
                    <button
                      key={photo.id}
                      type="button"
                      onClick={() => {
                        setGalleryStartIndex(index);
                        setShowImageGallery(true);
                      }}
                      style={{
                        aspectRatio: '1',
                        borderRadius: 10,
                        overflow: 'hidden',
                        border: '1px solid #e2e8f0',
                        padding: 0,
                        cursor: 'pointer',
                        background: '#f8fafc',
                      }}
                    >
                      <img
                        src={photo.preview || (photo.url ? getBackendUrl(photo.url) : '')}
                        alt={photo.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </button>
                  ))}
              </div>
            ) : (
              <p className="muted" style={{ marginBottom: 24 }}>
                Nenhuma foto adicionada.
              </p>
            )}

            <h4 className="muted" style={{ marginBottom: 12, display: 'flex', gap: 6, alignItems: 'center' }}>
              <DocumentTextIcon style={{ width: 16, height: 16 }} /> Documentos
            </h4>
            {(vehicle.attachments || []).filter((a: any) => a.type === 'document').length > 0 ? (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Arquivo</th>
                      <th className="hide-m">Categoria</th>
                      <th className="hide-m">Tamanho</th>
                      <th style={{ textAlign: 'right' }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(vehicle.attachments || [])
                      .filter((a: any) => a.type === 'document')
                      .map((doc: any) => (
                        <tr key={doc.id}>
                          <td>{doc.name}</td>
                          <td className="hide-m">{attachmentCategoryLabel(doc.category)}</td>
                          <td className="hide-m mono">
                            {doc.size ? `${(doc.size / 1024).toFixed(0)} KB` : '—'}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <button
                              type="button"
                              className="link-btn"
                              onClick={() => {
                                if (!doc.url) return;
                                const link = document.createElement('a');
                                link.href = getBackendUrl(doc.url);
                                link.download = doc.name;
                                link.target = '_blank';
                                link.click();
                              }}
                            >
                              Baixar
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="muted">Nenhum documento adicionado.</p>
            )}
          </div>
          {showImageGallery && (
            <ImageGallery
              images={(vehicle.attachments || []).filter((a: any) => a.type === 'image')}
              onClose={() => setShowImageGallery(false)}
              initialIndex={galleryStartIndex}
            />
          )}
        </div>
      )}

      {activeTab === 'related' && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Ativos relacionados</div>
            <span className="muted">{related.length} registros</span>
          </div>
          {related.length === 0 ? (
            <div className="muted" style={{ padding: 32, textAlign: 'center' }}>
              Nenhum ativo relacionado encontrado.
            </div>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Ativo</th>
                    <th className="hide-m">Tipo</th>
                    <th className="hide-m">Status</th>
                    <th className="hide-m" style={{ textAlign: 'right' }}>
                      Valor
                    </th>
                    <th style={{ textAlign: 'right' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {related.map((r: any) => (
                    <tr key={r.id}>
                      <td>{r.description}</td>
                      <td className="hide-m">{assetTypeLabel(r.type)}</td>
                      <td className="hide-m">
                        <StatusChip
                          label={formatStatus(r.status)}
                          tone={toneFor(String(r.status))}
                        />
                      </td>
                      <td className="amount hide-m" style={{ textAlign: 'right' }}>
                        {formatCurrency(Number(r.currentValue || 0))}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          type="button"
                          className="link-btn"
                          onClick={() => {
                            if (r.type === 'vehicle') navigate(`/vehicles/${r.id}`);
                            else if (r.type === 'aircraft') navigate(`/aircrafts/${r.id}`);
                            else if (r.type === 'vessel') navigate(`/vessels/${r.id}`);
                            else navigate(`/assets/${r.id}`);
                          }}
                        >
                          Abrir
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Excluir veículo"
        message="Tem certeza que deseja excluir este veículo do estoque? Esta ação não pode ser desfeita."
        onConfirm={() => {
          deleteMutation.mutate();
          setShowDeleteModal(false);
        }}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
        isLoading={deleteMutation.isLoading}
      />
    </DetailShell>
  );
};

export default VehicleDetail;
